import React from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Award,
  Users,
  TrendingUp,
  Heart,
  Target,
  Lightbulb,
  ArrowRight
} from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-purple-900 to-blue-900 text-white py-20">
        <div className="max-w-5xl mx-auto px-4">
          <Badge className="mb-6 bg-purple-500/20 text-purple-200 border-purple-400">
            👋 Notre Histoire
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            À Propos d'AmsWeb
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl leading-relaxed">
            Depuis 5 ans, je partage mon expertise crypto et MLM pour aider les gens à construire 
            leur liberté financière avec transparence et authenticité.
          </p>
        </div>
      </section>

      {/* Histoire */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Comment Tout a Commencé
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Il y a 5 ans, j'ai commencé mon parcours dans l'univers des cryptomonnaies. 
                  Passionné mais prudent, j'ai suivi une <strong>formation certifiée avec une personne 
                  agréée par l'AMF</strong> pour maîtriser les bases et éviter les pièges.
                </p>
                <p>
                  Rapidement, j'ai découvert le potentiel du MLM crypto et de la DeFi. 
                  Contrairement au MLM traditionnel, ici tout est <strong>transparent, automatisé 
                  et vérifiable</strong> grâce à la blockchain.
                </p>
                <p>
                  Aujourd'hui, je travaille avec un <strong>groupe d'investisseurs privés</strong> 
                  pour renforcer le phénomène de groupe et maximiser les résultats de chacun.
                </p>
                <p className="text-blue-600 font-semibold">
                  Ma mission : vous accompagner personnellement avec les opportunités que 
                  j'utilise moi-même et dont je peux prouver les résultats.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=600&h=700&fit=crop" 
                alt="Équipe AmsWeb - Crypto & Blockchain"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-blue-600 to-purple-600 text-white p-6 rounded-xl shadow-xl">
                <p className="text-3xl font-bold">5 ans</p>
                <p className="text-sm">d'expérience crypto</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Notre Mission
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Vous accompagner vers la liberté financière avec des opportunités vérifiées, 
              une formation solide et un accompagnement personnalisé
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: "Transparence Totale",
                description: "Pas de fausses promesses. Nous présentons les opportunités telles qu'elles sont, risques inclus."
              },
              {
                icon: Users,
                title: "Communauté Avant Tout",
                description: "Nous gagnons quand vous gagnez. Votre succès est notre priorité #1."
              },
              {
                icon: Lightbulb,
                title: "Éducation Continue",
                description: "Formations, webinaires, masterclasses : nous vous formons en permanence."
              }
            ].map((item, index) => (
              <Card key={index} className="border-0 shadow-xl text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Mes Valeurs en Chiffres
            </h2>
            <p className="text-xl text-slate-600">Uniquement des faits vérifiables</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Award, number: "5 ans", label: "Expérience Crypto", color: "from-blue-500 to-cyan-600" },
              { icon: Users, number: "AMF", label: "Formation Certifiée", color: "from-green-500 to-emerald-600" },
              { icon: TrendingUp, number: "100%", label: "Transparence Totale", color: "from-purple-500 to-pink-600" },
              { icon: Heart, number: "1-on-1", label: "Accompagnement Perso", color: "from-orange-500 to-red-600" }
            ].map((stat, index) => (
              <Card key={index} className="border-0 shadow-xl text-center hover:shadow-2xl transition-all">
                <CardContent className="p-8">
                  <div className={`w-16 h-16 bg-gradient-to-br ${stat.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <stat.icon className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                    {stat.number}
                  </p>
                  <p className="text-slate-600">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-purple-900 text-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Nos Valeurs</h2>
            <p className="text-xl text-blue-100">Ce qui guide chacune de nos décisions</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Intégrité",
                description: "Je ne recommande que des opportunités que j'utilise personnellement et dont je peux prouver les résultats."
              },
              {
                title: "Excellence",
                description: "Formation solide (AMF), collaboration avec investisseurs privés, accompagnement personnalisé de qualité."
              },
              {
                title: "Innovation",
                description: "Toujours à la pointe des nouvelles technologies blockchain et DeFi."
              },
              {
                title: "Authenticité",
                description: "Zéro mensonge, zéro promesse irréaliste. Uniquement des opportunités testées et approuvées."
              }
            ].map((value, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <h3 className="text-2xl font-bold mb-3">{value.title}</h3>
                <p className="text-blue-100">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">
            Prêt à Transformer Votre Vie ?
          </h2>
          <p className="text-xl text-slate-600 mb-8">
            Bénéficiez d'un accompagnement personnalisé et d'opportunités vérifiées
          </p>
          <Link to={createPageUrl("Contact")}>
            <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg px-10 py-7 shadow-xl">
              Commencer Maintenant
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
